import { useState, useEffect, memo } from 'react';
import { Route, Switch } from 'react-router-dom';
import { MenuAction } from 'models/types';
import { get } from 'lodash';
import AdminApp from 'kosmos/AdminApp';
import AdminHomePage from 'kosmos/pages/home/AdminHomePage';
import AdminUsersPage from 'kosmos/pages/user/AdminUsersPage';
import OrganisationsPage from 'kosmos/pages/organisation/OrganisationsPage';
import OrganisationDetailPage from 'kosmos/pages/organisation/OrganisationDetailPage';
import TransactionListPage from './pages/transaction/transactionListPage';
import CompanyListsPage from 'kosmos/pages/companyList/CompanyListsPage';
import CompanyListDetailPage from 'kosmos/pages/companyList/CompanyListDetailPage';
import ActivitiesPage from 'kosmos/pages/ActivitiesPage';
import AuditLogSummaryListPage from 'kosmos/pages/AuditLogSummaryListPage';
import ScoredCompaniesPage from 'kosmos/pages/scoredCompany/TfpScoredCompaniesPage';
import AdminScoredCompaniesPage from 'kosmos/pages/scoredCompany/AdminScoredCompaniesPage';
import NotFoundPage from 'pages/NotFoundPage';
import Splash from 'components/Splash';
import TfpApp from 'kosmos/TfpApp';
import TfpHomePage from 'kosmos/pages/home/TfpHomePage';
import TfpCompanyReportPage from 'kosmos/pages/report/TfpCompanyReportPage';
import TfpCombinedCompanyReportPage from 'kosmos/pages/report/TfpCombinedCompanyReportPage';
import CurrentUser from 'auth/currentUser';
import { GetCurrent, Logout } from 'services/user';
import { AccountInfo } from '@azure/msal-browser';
import { AddCredits } from 'kosmos/pages/AddCredits';
import UserDetailPage from './pages/user/UserDetailPage';
import SmartGovernanceAssignPage from 'kosmos/pages/smartGovernance/SmartGovernanceAssignPage';
import SmartGovernanceListPage from './pages/smartGovernance/SmartGovernanceListPage';
import SmartGovernanceDetailPage from './pages/smartGovernance/SmartGovernanceDetailPage';
import QuestionnairePage from './SmeApp/_questionnairePage';
import ConfirmAndSubmitPage from './SmeApp/_confirmAndSubmitPage';
import SmeQuestionnaireApp from './SmeApp/smeQuestionnaireApp';
import MySmartGovernances from './SmeApp/_mySmartGovernances';
import { TempTasksPage } from './pages/tempTasks';
import WomeApplicationListPage from './pages/womeApplication/womeApplicationListPage';
import WomeConfirmAndSubmitPage from './pages/womeApplication/womeConfirmAndSubmitPage';
import NewTransactionPage from './pages/transaction/newTransactionPage';
import SmeLayoutApp from './SmeApp/smeLayoutApp';
import { useMsal } from '@azure/msal-react';
import TradesunAiHomePage from './pages/home/TradesunAiHomePage';

interface Props {
  accountInfo: AccountInfo;
}

function ProtectedApp(props: Props): JSX.Element {
  const { instance } = useMsal();
  const { accountInfo } = props;
  const currentUser = CurrentUser.getInstance();
  const [requireUpdateCurrentUser, setRequireUpdateCurrentUser] = useState(true);

  const handleLogout = () => {
    Logout(instance);
  };

  useEffect(() => {
    const fetchUser = async () => {
      // fetch company name and cache in localstorage
      if (requireUpdateCurrentUser) {
        const user = await GetCurrent();
        if (user) {
          if (!user.organisation) {
            handleLogout();
          }
          currentUser.setUser(JSON.stringify(user));
          currentUser.setRole(user.userRole);
          setRequireUpdateCurrentUser(false);
        } else {
          handleLogout();
        }
      }
    };
    void fetchUser();
  }, []);

  const getRoleFromAccount = (accountInfo: AccountInfo): string => {
    const role = (get(accountInfo, ['idTokenClaims', 'extension_atlasRole']) as string) || 'sme';
    return role.toLowerCase();
  };

  const renderSmeProtected = (): JSX.Element => {
    const smeLogoUrl = currentUser.getOrganisationAttr('logoUrl');
    return (
      <Switch>
        <Route
          path="/smart_governances/:id"
          render={({
            match: {
              url,
              params: { id },
            },
          }) => {
            return (
              <SmeQuestionnaireApp companyId={id} logoUrl={smeLogoUrl}>
                <Switch>
                  <Route path={`${url}/`} component={SmartGovernanceDetailPage} exact />
                  <Route
                    path={`${url}/questionnaire/confirm-and-submit`}
                    render={() => {
                      return <ConfirmAndSubmitPage companyId={id} alias="confirm-and-submit" />;
                    }}
                  />
                  <Route
                    path={`${url}/questionnaire/:alias`}
                    render={({ match }) => {
                      return <QuestionnairePage companyId={id} alias={match.params.alias} />;
                    }}
                  />
                  <Route path="*" component={NotFoundPage} />
                </Switch>
              </SmeQuestionnaireApp>
            );
          }}
        />
        <Route
          path="/"
          render={() => {
            return (
              <SmeLayoutApp logoUrl={smeLogoUrl}>
                <Switch>
                  <Route path="/home" exact={true} component={MySmartGovernances} />
                  <Route path="/smart_governances" exact={true} component={MySmartGovernances} />
                  <Route path="/reports/:id" exact={true} component={TfpCompanyReportPage} />
                  <Route
                    path="/combined-reports/:id"
                    exact={true}
                    component={TfpCombinedCompanyReportPage}
                  />
                  <Route path="*" component={NotFoundPage} />
                </Switch>
              </SmeLayoutApp>
            );
          }}
        />
      </Switch>
    );
  };

  const renderAdminProtected = (): JSX.Element => {
    return (
      <Switch>
        <Route
          path="/wome_companies/:id"
          render={({
            match: {
              url,
              params: { id },
            },
          }) => {
            return (
              <SmeQuestionnaireApp companyId={id}>
                <Switch>
                  <Route path={`${url}/`} component={SmartGovernanceDetailPage} exact />
                  <Route
                    path={`${url}/questionnaire/wome-confirm-and-submit`}
                    render={() => {
                      return (
                        <WomeConfirmAndSubmitPage companyId={id} alias="wome-confirm-and-submit" />
                      );
                    }}
                  />
                  <Route
                    path={`${url}/questionnaire/:alias`}
                    render={({ match }) => {
                      return (
                        <QuestionnairePage
                          companyId={id}
                          alias={match.params.alias || 'wome-company-information'}
                        />
                      );
                    }}
                  />

                  <Route path="*" component={NotFoundPage} />
                </Switch>
              </SmeQuestionnaireApp>
            );
          }}
        />
        <Route
          path="/"
          render={() => {
            return (
              <AdminApp onRenderTfp={(actions?: MenuAction[]) => renderTfpProtected(actions)}>
                <Switch>
                  {/* <Route path="/home" exact={true} component={TfpHomePage} /> */}
                  <Route path="/home" exact={true} component={TradesunAiHomePage} />
                  <Route path="/smart_governances" component={SmartGovernanceListPage} />
                  <Route path="/wome_applications" component={WomeApplicationListPage} />
                  <Route
                    path="/companies/:id/questionnaire/confirm-and-submit"
                    exact={true}
                    component={ConfirmAndSubmitPage}
                  />
                  <Route
                    path="/companies/:id/questionnaire/:alias"
                    exact={true}
                    component={QuestionnairePage}
                  />
                  <Route
                    path="/companies/:id/sg_assign"
                    exact={true}
                    component={SmartGovernanceAssignPage}
                  />
                  <Route path="/companies" component={AdminHomePage} />
                  <Route path="/users/:id" exact={true} component={UserDetailPage} />
                  <Route path="/users" component={AdminUsersPage} />
                  <Route
                    path="/scored_companies"
                    exact={true}
                    component={AdminScoredCompaniesPage}
                  />
                  <Route path="/add_credits" exact={true} component={AddCredits} />
                  <Route
                    path="/organisations/:id"
                    exact={true}
                    component={OrganisationDetailPage}
                  />
                  <Route path="/organisations" exact={true} component={OrganisationsPage} />
                  <Route path="/transactions/new" component={NewTransactionPage} />
                  <Route
                    path="/transactions"
                    exact={true}
                    render={() => <TransactionListPage isAdmin />}
                  />
                  <Route path="/companyLists/:id" exact={true} component={CompanyListDetailPage} />
                  <Route
                    path="/companyLists"
                    exact={true}
                    render={() => <CompanyListsPage isAdmin />}
                  />
                  <Route path="/reports/:id" exact={true} component={TfpCompanyReportPage} />
                  <Route
                    path="/combined-reports/:id"
                    exact={true}
                    component={TfpCombinedCompanyReportPage}
                  />
                  <Route path="/temp_tasks" exact={true} component={TempTasksPage} />
                  <Route path="*" component={NotFoundPage} />
                </Switch>
              </AdminApp>
            );
          }}
        />
      </Switch>
    );
  };

  const renderTfpProtected = (actions?: MenuAction[]): JSX.Element => {
    return (
      <Switch>
        <Route
          path="/wome_companies/:id"
          render={({
            match: {
              url,
              params: { id },
            },
          }) => {
            return (
              <SmeQuestionnaireApp companyId={id}>
                <Switch>
                  <Route path={`${url}/`} component={SmartGovernanceDetailPage} exact />
                  <Route
                    path={`${url}/questionnaire/wome-confirm-and-submit`}
                    render={() => {
                      return (
                        <WomeConfirmAndSubmitPage companyId={id} alias="wome-confirm-and-submit" />
                      );
                    }}
                  />
                  <Route
                    path={`${url}/questionnaire/:alias`}
                    render={({ match }) => {
                      return (
                        <QuestionnairePage
                          companyId={id}
                          alias={match.params.alias || 'wome-company-information'}
                        />
                      );
                    }}
                  />
                  <Route path="*" component={NotFoundPage} />
                </Switch>
              </SmeQuestionnaireApp>
            );
          }}
        />
        <Route
          path="/"
          render={() => {
            return (
              <TfpApp actions={actions}>
                <Switch>
                  {/* <Route path="/home" exact={true} component={TfpHomePage} /> */}
                  <Route path="/home" exact={true} component={TradesunAiHomePage} />
                  <Route path="/smart_governances" component={SmartGovernanceListPage} />
                  <Route path="/wome_applications" component={WomeApplicationListPage} />
                  <Route
                    path="/organisations/:id"
                    exact={true}
                    component={OrganisationDetailPage}
                  />
                  <Route
                    path="/companies/:id/sg_assign"
                    exact={true}
                    component={SmartGovernanceAssignPage}
                  />
                  <Route path="/companies" component={TfpHomePage} />
                  <Route path="/companyLists/:id" exact={true} component={CompanyListDetailPage} />
                  <Route path="/companyLists" exact={true} component={CompanyListsPage} />
                  <Route path="/activities" exact={true} component={ActivitiesPage} />
                  <Route path="/audit_summary" exact={true} component={AuditLogSummaryListPage} />
                  <Route path="/scored_companies" exact={true} component={ScoredCompaniesPage} />
                  <Route path="/reports/:id" exact={true} component={TfpCompanyReportPage} />
                  <Route
                    path="/combined-reports/:id"
                    exact={true}
                    component={TfpCombinedCompanyReportPage}
                  />
                  <Route path="/transactions/new" component={NewTransactionPage} />
                  <Route path="/transactions" component={TransactionListPage} />
                  <Route path="*" component={NotFoundPage} />
                </Switch>
              </TfpApp>
            );
          }}
        />
      </Switch>
    );
  };

  const renderRoleApp = () => {
    const role = getRoleFromAccount(accountInfo);
    if (role === 'admin') {
      return renderAdminProtected();
    } else if (role === 'tfp' || role === 'orgadmin') {
      return renderTfpProtected();
    } else if (role === 'sme') {
      return renderSmeProtected();
    }
  };

  // start a websocket connection when a user is login
  return (
    <>
      {requireUpdateCurrentUser ? (
        <Splash name="coriolis" hide={!requireUpdateCurrentUser} debounce={750} />
      ) : (
        renderRoleApp()
      )}
    </>
  );
}

// should never re-render on props change
export default memo(ProtectedApp, () => true);
